import { Link, useParams } from "react-router-dom";
import {
  collections,
  toolCategories,
  toolsInCollection,
  allTools,
  accentClasses,
  type Accent,
  type ToolDef,
} from "../toolRegistry";
import Avatar from "../components/ui/Avatar";
import { useOrgCollections } from "../lib/useOrgProfiles";

/**
 * The inside of a collection: one person's tools, one topic's tools, or the
 * leftovers. Reached from the Tools landing page.
 *
 * It is a route rather than local state on purpose -- back button, deep links,
 * and bookmarks all work, which matters when someone wants to send a colleague
 * straight to "Amy's Data".
 */

function ToolCard({ tool, accent }: { tool: ToolDef; accent: Accent }) {
  const a = accentClasses[accent];
  return (
    <Link
      to={tool.live ? `/tools/${tool.id}` : "#"}
      className={`ui-card p-4 h-full flex gap-3 items-start transition duration-150 hover:shadow-lg2 hover:-translate-y-0.5 ${
        !tool.live ? "opacity-55 pointer-events-none" : ""
      }`}
    >
      <span className={`w-10 h-10 shrink-0 rounded-sm2 ${a.tint} flex items-center justify-center text-lg`}>
        {tool.icon}
      </span>
      <div className="min-w-0">
        <div className="font-bold flex items-center gap-2">
          {tool.name}
          {tool.live ? (
            <span className="pill bg-brand-tint text-brand-deep">Live</span>
          ) : (
            <span className="pill bg-paper-2 text-ink-faint">Work in progress</span>
          )}
        </div>
        <p className="text-ink-soft text-[13px] mt-0.5 line-clamp-2">{tool.description}</p>
      </div>
    </Link>
  );
}

type Resolved = {
  title: string;
  subtitle?: string;
  accent: Accent;
  initials?: string;
  photo?: string;
  icon?: string;
  tools: ToolDef[];
};

function resolve(
  groupId: string | undefined,
  people: typeof collections
): Resolved | null {
  if (!groupId) return null;

  const person = people.find((c) => c.id === groupId);
  if (person) {
    return {
      title: person.label,
      subtitle: person.person,
      accent: person.accent,
      initials: person.initials,
      photo: person.photo,
      tools: toolsInCollection(person),
    };
  }

  if (groupId === "everything-else") {
    const claimed = new Set(people.flatMap((c) => c.toolIds));
    return {
      title: "Everything else",
      subtitle: "Not in anyone's collection",
      accent: "info",
      icon: "⋯",
      tools: allTools.filter((t) => !claimed.has(t.id)),
    };
  }

  if (groupId.startsWith("topic-")) {
    const name = decodeURIComponent(groupId.slice("topic-".length));
    const cat = toolCategories.find((c) => c.name === name);
    if (cat) {
      return {
        title: cat.name,
        subtitle: "Grouped by what the tool does",
        accent: cat.accent,
        icon: cat.tools[0]?.icon ?? "🧰",
        tools: cat.tools,
      };
    }
  }

  return null;
}

export default function ToolGroupPage() {
  const { groupId } = useParams();
  const people = useOrgCollections();
  const group = resolve(groupId, people);

  if (!group) {
    return (
      <div>
        <Link to="/tools" className="text-brand text-sm font-semibold">
          ← Tools
        </Link>
        <p className="mt-4 text-ink-soft">That collection no longer exists.</p>
      </div>
    );
  }

  const a = accentClasses[group.accent];

  return (
    <div>
      <Link to="/tools" className="text-brand text-sm font-semibold hover:underline">
        ← Tools
      </Link>

      <div className="flex items-center gap-3 mt-3 mb-6 animate-[fadeUp_.3s_ease-out_both]">
        {group.initials ? (
          <Avatar
            initials={group.initials}
            accent={group.accent}
            photo={group.photo}
            alt={group.subtitle}
            size="lg"
            shape="rounded"
          />
        ) : (
          <span className={`w-12 h-12 shrink-0 rounded-full ${a.tint} flex items-center justify-center text-xl`}>
            {group.icon}
          </span>
        )}
        <div className="min-w-0">
          <h2 className="font-extrabold text-[26px] tracking-tight leading-tight">{group.title}</h2>
          {group.subtitle && <p className="text-ink-soft text-[13px]">{group.subtitle}</p>}
        </div>
        <span className="text-ink-faint text-xs font-mono ml-auto">
          {group.tools.length} tool{group.tools.length === 1 ? "" : "s"}
        </span>
      </div>

      {group.tools.length === 0 ? (
        <p className="text-ink-soft text-sm">
          Nothing assigned to this collection yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 min-[640px]:grid-cols-2 gap-3">
          {group.tools.map((tool, i) => (
            <div
              key={tool.id}
              className="animate-[fadeUp_.35s_ease-out_both] h-full"
              style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
            >
              <ToolCard tool={tool} accent={group.accent} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
